// TasksList.js — Applicant follow-up tasks (Frappe CRM-style)
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './TasksList.css';

const PRIORITIES = [
  { value: 'low',    label: 'Low',    color: '#94a3b8' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high',   label: 'High',   color: '#f97316' },
  { value: 'urgent', label: 'Urgent', color: '#ef4444' },
];

const STATUS_OPTS = [
  { value: 'open',        label: 'Open'        },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done',        label: 'Done'        },
  { value: 'cancelled',   label: 'Cancelled'   },
];

const defaultForm = {
  title: '',
  description: '',
  due_date: '',
  priority: 'medium',
  assigned_to: '',
  created_by: '',
};

function getPriorityInfo(p) {
  return PRIORITIES.find((x) => x.value === p) || PRIORITIES[1];
}

function isOverdue(due_date, status) {
  if (!due_date || status === 'done' || status === 'cancelled') return false;
  return new Date(due_date) < new Date(new Date().toDateString());
}

function formatDueDate(due_date) {
  if (!due_date) return null;
  const d = new Date(due_date);
  const today = new Date(new Date().toDateString());
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Due tomorrow';
  if (diff === -1) return 'Due yesterday';
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff <= 7) return `Due in ${diff}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function TasksList({ applicantId }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('active'); // 'active' | 'done' | 'all'
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/tasks`);
      if (res.ok) setTasks(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [applicantId]);

  useEffect(() => { if (applicantId) load(); }, [applicantId, load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { await load(); setShowForm(false); setForm(defaultForm); }
      else { const e = await res.json(); setError(e.error || 'Failed to save.'); }
    } catch { setError('Network error.'); }
    finally { setSaving(false); }
  };

  const patchTask = async (id, patch) => {
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      setTasks((prev) => prev.map((t) => t.id === id ? { ...t, ...patch } : t));
    } catch (e) { console.error(e); }
  };

  const toggleDone = (task) => {
    const newStatus = task.status === 'done' ? 'open' : 'done';
    patchTask(task.id, { status: newStatus });
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/tasks/${id}`, { method: 'DELETE' });
      setTasks((prev) => prev.filter((t) => t.id !== id));
    } catch (e) { console.error(e); }
  };

  const startEdit = (task) => {
    setEditingId(task.id);
    setEditForm({
      title: task.title,
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority,
      assigned_to: task.assigned_to || '',
    });
  };

  const saveEdit = async (id) => {
    await patchTask(id, editForm);
    setEditingId(null);
  };

  const filtered = tasks.filter((t) => {
    if (filterStatus === 'active') return t.status !== 'done' && t.status !== 'cancelled';
    if (filterStatus === 'done') return t.status === 'done';
    return true;
  });

  const openCount = tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length;
  const overdueCount = tasks.filter((t) => isOverdue(t.due_date, t.status)).length;

  return (
    <div className="tasks-list">
      {/* Header */}
      <div className="tasks-header">
        <div className="tasks-header-left">
          <h3 className="tasks-title">Tasks</h3>
          <div className="tasks-counts">
            {openCount > 0 && (
              <span className="tasks-count-badge open">{openCount} open</span>
            )}
            {overdueCount > 0 && (
              <span className="tasks-count-badge overdue">{overdueCount} overdue</span>
            )}
          </div>
        </div>
        <button
          className="tasks-add-btn"
          onClick={() => { setShowForm((p) => !p); setError(''); }}
        >
          {showForm ? '✕ Cancel' : '+ Add Task'}
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form className="tasks-form" onSubmit={handleAdd}>
          <div className="tasks-form-row">
            <input
              className="tasks-input task-title-input"
              type="text"
              placeholder="Task title *"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              autoFocus
            />
          </div>
          <div className="tasks-form-row three-col">
            <div className="tasks-form-group">
              <label>Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
              >
                {PRIORITIES.map((pr) => <option key={pr.value} value={pr.value}>{pr.label}</option>)}
              </select>
            </div>
            <div className="tasks-form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))}
              />
            </div>
            <div className="tasks-form-group">
              <label>Assign To</label>
              <input
                type="text"
                placeholder="Agent name"
                value={form.assigned_to}
                onChange={(e) => setForm((p) => ({ ...p, assigned_to: e.target.value }))}
              />
            </div>
          </div>
          <div className="tasks-form-row">
            <textarea
              className="tasks-input"
              placeholder="Description (optional)"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
          {error && <div className="tasks-error">{error}</div>}
          <div className="tasks-form-actions">
            <button type="button" className="tasks-btn-cancel" onClick={() => { setShowForm(false); setForm(defaultForm); setError(''); }}>Cancel</button>
            <button type="submit" className="tasks-btn-save" disabled={saving}>{saving ? 'Saving…' : 'Save Task'}</button>
          </div>
        </form>
      )}

      {/* Filter Tabs */}
      {tasks.length > 0 && (
        <div className="tasks-filter-tabs">
          {[
            { key: 'active', label: 'Active' },
            { key: 'done',   label: 'Done'   },
            { key: 'all',    label: 'All'    },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`tasks-filter-tab ${filterStatus === tab.key ? 'active' : ''}`}
              onClick={() => setFilterStatus(tab.key)}
            >
              {tab.label}
              <span className="tasks-filter-count">
                {tab.key === 'active'
                  ? tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length
                  : tab.key === 'done'
                  ? tasks.filter((t) => t.status === 'done').length
                  : tasks.length}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Task Items */}
      <div className="tasks-items">
        {loading ? (
          <div className="tasks-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="tasks-empty">
            {tasks.length === 0
              ? 'No tasks yet. Click "+ Add Task" to create a follow-up.'
              : 'No tasks in this category.'}
          </div>
        ) : filtered.map((task) => {
          const pInfo = getPriorityInfo(task.priority);
          const overdue = isOverdue(task.due_date, task.status);
          const isDone = task.status === 'done';
          const isEditing = editingId === task.id;

          return (
            <div key={task.id} className={`task-item ${isDone ? 'done' : ''} ${overdue ? 'overdue' : ''}`}>
              {isEditing ? (
                <div className="task-edit-form">
                  <input
                    className="tasks-input task-title-input"
                    value={editForm.title}
                    onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                  />
                  <div className="tasks-form-row three-col">
                    <div className="tasks-form-group">
                      <label>Priority</label>
                      <select value={editForm.priority} onChange={(e) => setEditForm((p) => ({ ...p, priority: e.target.value }))}>
                        {PRIORITIES.map((pr) => <option key={pr.value} value={pr.value}>{pr.label}</option>)}
                      </select>
                    </div>
                    <div className="tasks-form-group">
                      <label>Due Date</label>
                      <input type="date" value={editForm.due_date} onChange={(e) => setEditForm((p) => ({ ...p, due_date: e.target.value }))} />
                    </div>
                    <div className="tasks-form-group">
                      <label>Assign To</label>
                      <input type="text" value={editForm.assigned_to} onChange={(e) => setEditForm((p) => ({ ...p, assigned_to: e.target.value }))} />
                    </div>
                  </div>
                  <textarea className="tasks-input" rows={2} value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
                  <div className="tasks-form-actions">
                    <button type="button" className="tasks-btn-cancel" onClick={() => setEditingId(null)}>Cancel</button>
                    <button type="button" className="tasks-btn-save" onClick={() => saveEdit(task.id)}>Save</button>
                  </div>
                </div>
              ) : (
                <div className="task-item-content">
                  {/* Checkbox */}
                  <button
                    className={`task-checkbox ${isDone ? 'checked' : ''}`}
                    onClick={() => toggleDone(task)}
                    title={isDone ? 'Mark as open' : 'Mark as done'}
                  >
                    {isDone && '✓'}
                  </button>

                  {/* Main */}
                  <div className="task-main">
                    <div className="task-top-row">
                      <span className={`task-title-text ${isDone ? 'strikethrough' : ''}`}>{task.title}</span>
                      <div className="task-badges">
                        <span
                          className="task-priority-badge"
                          style={{ background: pInfo.color + '20', color: pInfo.color, borderColor: pInfo.color + '50' }}
                        >
                          {pInfo.label}
                        </span>
                        {task.due_date && (
                          <span className={`task-due-badge ${overdue ? 'overdue' : ''}`}>
                            {formatDueDate(task.due_date)}
                          </span>
                        )}
                        {task.assigned_to && (
                          <span className="task-assignee-badge">👤 {task.assigned_to}</span>
                        )}
                      </div>
                    </div>
                    {task.description && (
                      <p className="task-description">{task.description}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="task-actions">
                    <select
                      className="task-status-select"
                      value={task.status}
                      onChange={(e) => patchTask(task.id, { status: e.target.value })}
                      title="Change status"
                    >
                      {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <button className="task-edit-btn" title="Edit" onClick={() => startEdit(task)}>✏️</button>
                    <button className="task-delete-btn" title="Delete" onClick={() => handleDelete(task.id)}>×</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
